import React, { useEffect } from 'react'
import { ConsumerPage } from '../../model'
import { Trans, useTranslation } from 'next-i18next'
import { Head } from '../../component'
import { TrackPageViewed, useAmplitudeWithEnduringEventProperties } from '../../integrations/amplitude'
import { Heading, Link, Loader, Text } from '@afterpaytouch/core'
import { useRouter } from 'next/router'
import { useConsumerSizes } from '../../utils/responsive'
import { LoginIdentity } from '../../component/LoginIdentity'
import { useIsConsumerAuthenticated } from '../../state'
import { useConsumerFeatureFlagReady, useLazyGetAccountQuery } from '../../state/account/account.hooks'
import { Route } from '../../config/router'
import { TrackingEvent } from '../../model/amplitude'
import { usePortalMigrationEnabled } from '../../hooks'
import { getUserFromStorage } from '../../utils/userStorage'
import { LaunchDarklyFlags, useFlag } from '../../hooks/useFlag'
import { setApPortalToken } from '../../utils/tokenStorage'

const I18N_NAMESPACE = ['common', 'login']

interface IndexPageProps {
  token?: string | null
}

export const IndexPage: ConsumerPage<IndexPageProps> = (props) => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const pageTitle = `${t('login:title')} | ${t('common:brandName')}`
  const router = useRouter()
  const { headingSize } = useConsumerSizes()
  const hasFeatureFlagsReady = useConsumerFeatureFlagReady()
  const loginMigrationEnabled = usePortalMigrationEnabled()
  const isConsumerAuthenticated = useIsConsumerAuthenticated()
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  const [triggerAccount, { isSuccess: isAccountSuccess, isLoading }] = useLazyGetAccountQuery()
  const isPortalSignupEnabled = useFlag(LaunchDarklyFlags.EnabledPortalSignup)
  const showLoading = !hasFeatureFlagsReady || !loginMigrationEnabled || isLoading
  const storedUser = getUserFromStorage()

  useEffect(() => {
    router.prefetch(Route.HOME)
    router.prefetch(Route.LOGGED_IN)
    router.prefetch(Route.LOGIN_PASSWORD)
  }, [router])

  useEffect(() => {
    const { token } = props
    if (token) {
      setApPortalToken(token)
    }
  }, [props])

  useEffect(() => {
    if (isConsumerAuthenticated || isAccountSuccess) {
      router.push(Route.HOME)
    }
  }, [isConsumerAuthenticated, isAccountSuccess, router])

  useEffect(() => {
    if (storedUser != null && hasFeatureFlagsReady) {
      triggerAccount()
    }
  }, [storedUser, hasFeatureFlagsReady])

  useEffect(() => {
    if (hasFeatureFlagsReady && typeof loginMigrationEnabled !== 'undefined') {
      if (!loginMigrationEnabled) {
        router.push(Route.HOME)
      }
    }
  }, [loginMigrationEnabled, hasFeatureFlagsReady])

  return showLoading ? (
    <>
      <Head title={pageTitle} />
      <Loader alignCenter />
      <TrackPageViewed eventProps={{ loginMigrationEnabled, loading: true }} />
    </>
  ) : (
    <>
      <Head title={pageTitle} />
      <Heading size={headingSize}>{t(isPortalSignupEnabled ? 'login:title-sign-up' : 'login:title')}</Heading>
      <LoginIdentity />
      {!isPortalSignupEnabled && (
        <div className='mt-2 text-center'>
          <Text size='XS'>
            <Trans
              i18nKey={t('login:download')}
              components={{
                DownloadLink: (
                  <Link
                    inline
                    kind='Underline'
                    href={t('common:urls.mobilePageBranchLink')}
                    target='_blank'
                    rel='noreferrer'
                    onClick={() => logEvent(TrackingEvent.CLICK_DOWNLOAD_MOBILE_APP)}
                  />
                ),
              }}
            />
          </Text>
        </div>
      )}
      <div className='mt-6 text-center'>
        <Link
          kind='Underline'
          href={t('common:urls.merchantPortalLoginUrl')}
          onClick={() => {
            logEvent(TrackingEvent.CLICKED_RETAILER_LINK)
          }}
        >
          {t('login:retailer')}
        </Link>
      </div>
      <TrackPageViewed eventProps={{ loginMigrationEnabled, loading: false }} />
    </>
  )
}
